import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatErr } from "@/lib/api";
import { Sparkles, Plus, Trash2, BadgeCheck, Check, Circle, Home, ShoppingBag, Briefcase, Edit3, X } from "lucide-react";
import PhotoDrop from "@/components/PhotoDrop";
import CategoryPicker from "@/components/CategoryPicker";
import VideoReel from "@/components/VideoReel";
import ImagePositioner from "@/components/ImagePositioner";
import { PAYMENT_METHODS } from "@/lib/payments";

const CATEGORIES = []; // legacy, replaced by CategoryPicker

export default function EditProfile() {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [svcForm, setSvcForm] = useState({ title:"", description:"", price:50, duration_min:60, image:"", delivery_type:"in_person" });
  const [editingSvcId, setEditingSvcId] = useState(null);
  const [aiPricing, setAiPricing] = useState("");
  const [aiPriceLoading, setAiPriceLoading] = useState(false);
  const [skillInput, setSkillInput] = useState("");
  const [err, setErr] = useState("");

  const [portfolio, setPortfolio] = useState([]);
  const [portImage, setPortImage] = useState("");
  const [portCaption, setPortCaption] = useState("");

  const [verif, setVerif] = useState(null);
  const reloadVerif = () => api.get("/verification/status").then(r=>setVerif(r.data)).catch(()=>{});
  useEffect(()=>{ reloadVerif(); }, [user?.id]);
  useEffect(()=>{ if (!saving) reloadVerif(); }, [saving]);

  const [products, setProducts] = useState([]);
  const [prodForm, setProdForm] = useState({
    title:"", description:"", price:15, image:"", images:[], file_url:"", product_type:"digital",
    shipping_cost:"", shipping_free_over:"", ships_from:"", ships_to:"", estimated_delivery:"",
    tax_rate:"", tax_included:false,
  });
  const [editingProdId, setEditingProdId] = useState(null);
  useEffect(()=>{ if (user) api.get("/products/mine").then(r=>setProducts(r.data)).catch(()=>{}); }, [user?.id]);
  const addProduct = async (e) => {
    e.preventDefault();
    const imgs = (prodForm.images || []).filter(Boolean).slice(0, 5);
    const payload = {
      ...prodForm,
      price: Number(prodForm.price),
      images: imgs,
      image: prodForm.image || imgs[0] || "",
      shipping_cost: prodForm.shipping_cost === "" ? 0 : Number(prodForm.shipping_cost),
      shipping_free_over: prodForm.shipping_free_over === "" ? null : Number(prodForm.shipping_free_over),
      tax_rate: prodForm.tax_rate === "" ? 0 : Number(prodForm.tax_rate),
    };
    if (editingProdId) {
      const { data } = await api.patch(`/products/${editingProdId}`, payload);
      setProducts(products.map(p => p.id === editingProdId ? data : p));
      setEditingProdId(null);
    } else {
      const { data } = await api.post("/products", payload);
      setProducts([data, ...products]);
    }
    setProdForm({
      title:"", description:"", price:15, image:"", images:[], file_url:"", product_type:"digital",
      shipping_cost:"", shipping_free_over:"", ships_from:"", ships_to:"", estimated_delivery:"",
      tax_rate:"", tax_included:false,
    });
    await refreshUser();
  };
  const removeProduct = async (id) => {
    await api.delete(`/products/${id}`);
    setProducts(products.filter(p => p.id !== id));
  };
  const editProduct = (p) => {
    setEditingProdId(p.id);
    setProdForm({
      title: p.title || "", description: p.description || "",
      price: p.price ?? 15,
      image: p.image || "",
      images: (p.images && p.images.length ? p.images : (p.image ? [p.image] : [])).slice(0, 5),
      file_url: p.file_url || "",
      product_type: p.product_type || "digital",
      shipping_cost: p.shipping_cost ?? "",
      shipping_free_over: p.shipping_free_over ?? "",
      ships_from: p.ships_from || "",
      ships_to: p.ships_to || "",
      estimated_delivery: p.estimated_delivery || "",
      tax_rate: p.tax_rate ?? "",
      tax_included: !!p.tax_included,
    });
    setTimeout(() => document.querySelector('[data-testid="product-form"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 40);
  };
  const cancelEditProduct = () => {
    setEditingProdId(null);
    setProdForm({
      title:"", description:"", price:15, image:"", images:[], file_url:"", product_type:"digital",
      shipping_cost:"", shipping_free_over:"", ships_from:"", ships_to:"", estimated_delivery:"",
      tax_rate:"", tax_included:false,
    });
  };

  useEffect(()=>{ if (user) setForm({
    name: user.name||"", bio: user.bio||"", location: user.location||"",
    category: user.category||"",
    categories: user.categories?.length ? user.categories : (user.category ? [user.category] : []),
    skills: user.skills||[], avatar: user.avatar||"",
    cover: user.cover||"", years_experience: user.years_experience||0,
    avatar_position: user.avatar_position || "50% 50%",
    cover_position:  user.cover_position  || "50% 50%",
    languages: user.languages||["English"], is_hooman: true,
    payment_methods: user.payment_methods?.length ? user.payment_methods : ["card"],
    sells_bookings: user.sells_bookings !== false,
    sells_products: !!user.sells_products,
    email_prefs: {
      message: true, booking: true, application: true, interview: true, follow: true,
      like: false, save: false, review: true,
      ...(user.email_prefs || {}),
    },
  });}, [user]);

  const loadAll = async () => {
    if (!user) return;
    const [s, prof] = await Promise.all([
      api.get("/services/mine"),
      api.get(`/users/${user.id}`),
    ]);
    setServices(s.data);
    setPortfolio(prof.data.posts || []);
  };
  useEffect(()=>{ loadAll(); }, [user?.id]);

  if (!form) return <div className="p-10 text-center" data-testid="edit-loading">Loading…</div>;

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true); setErr("");
    try { await api.put("/users/me", form); await refreshUser(); }
    catch (e) { setErr(formatErr(e)); }
    finally { setSaving(false); }
  };

  const aiBio = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/bio", {
        name: form.name, skills: form.skills, category: form.category,
        years_experience: Number(form.years_experience)||0,
      });
      setForm({...form, bio: data.bio});
    } catch (e) { setErr(formatErr(e)); }
    finally { setAiLoading(false); }
  };

  const suggestPrice = async () => {
    setAiPriceLoading(true);
    try {
      const { data } = await api.post("/ai/pricing", {
        service_title: svcForm.title || "service",
        category: form.category, location: form.location,
        years_experience: Number(form.years_experience)||0,
        duration_min: Number(svcForm.duration_min)||60,
      });
      setAiPricing(data.suggestion);
    } catch (e) { setErr(formatErr(e)); }
    finally { setAiPriceLoading(false); }
  };

  const addSkill = () => {
    const s = skillInput.trim();
    if (!s) return;
    if (!form.skills.includes(s)) setForm({...form, skills:[...form.skills, s]});
    setSkillInput("");
  };

  const addService = async (e) => {
    e.preventDefault();
    const payload = {
      title: svcForm.title, description: svcForm.description,
      price: Number(svcForm.price), duration_min: Number(svcForm.duration_min),
      image: svcForm.image || "",
      delivery_type: svcForm.delivery_type || "in_person",
    };
    if (editingSvcId) {
      const { data } = await api.patch(`/services/${editingSvcId}`, payload);
      setServices(services.map(s => s.id === editingSvcId ? data : s));
      setEditingSvcId(null);
    } else {
      const { data } = await api.post("/services", payload);
      setServices([...services, data]);
    }
    setSvcForm({ title:"", description:"", price:50, duration_min:60, image:"", delivery_type:"in_person" });
    setAiPricing("");
    await refreshUser();
  };

  const editService = (s) => {
    setEditingSvcId(s.id);
    setSvcForm({
      title: s.title || "", description: s.description || "",
      price: s.price ?? 50, duration_min: s.duration_min ?? 60,
      image: s.image || "", delivery_type: s.delivery_type || "in_person",
    });
    if (typeof window !== "undefined") {
      setTimeout(() => document.querySelector('[data-testid="service-form"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 40);
    }
  };

  const cancelEditService = () => {
    setEditingSvcId(null);
    setSvcForm({ title:"", description:"", price:50, duration_min:60, image:"", delivery_type:"in_person" });
    setAiPricing("");
  };

  const removeService = async (id) => {
    await api.delete(`/services/${id}`);
    setServices(services.filter(s=>s.id!==id));
  };

  const addPortfolio = async (e) => {
    e.preventDefault();
    if (!portImage) return;
    const { data } = await api.post("/posts", { image: portImage, caption: portCaption || "New work" });
    setPortfolio([data, ...portfolio]);
    setPortImage(""); setPortCaption("");
  };

  const removePortfolio = async (id) => {
    if (!window.confirm("Remove this piece from your portfolio? This can't be undone.")) return;
    try {
      await api.delete(`/posts/${id}`);
      setPortfolio(portfolio.filter(p => p.id !== id));
    } catch (e) { setErr(formatErr(e)); }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 sm:px-8 py-10">
      <p className="overline mb-3">Your profile</p>
      <h1 className="h-display text-4xl mb-6">Make it feel like you.</h1>

      {/* Quick post shortcuts — one-click to list something new */}
      <div className="card p-5 mb-8" data-testid="quick-post-card">
        <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4">
          <div>
            <p className="overline">Post something</p>
            <p className="text-sm text-[#4A4A4A] mt-1">Add a new listing without leaving your profile.</p>
          </div>
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <Link to="/rentals/new" className="quick-post-tile" data-testid="quick-post-rental">
            <span className="quick-post-icon"><Home size={18}/></span>
            <span className="flex-1">
              <span className="block font-medium text-sm">Add a rental</span>
              <span className="block text-xs text-[#8A8A8A]">Space · vehicle · gear</span>
            </span>
            <Plus size={14} className="text-[#8A8A8A]"/>
          </Link>
          <Link to="/market/new" className="quick-post-tile" data-testid="quick-post-sale">
            <span className="quick-post-icon"><ShoppingBag size={18}/></span>
            <span className="flex-1">
              <span className="block font-medium text-sm">List something for sale</span>
              <span className="block text-xs text-[#8A8A8A]">Handmade · pre-owned · new</span>
            </span>
            <Plus size={14} className="text-[#8A8A8A]"/>
          </Link>
          <Link to="/jobs/new" className="quick-post-tile" data-testid="quick-post-job">
            <span className="quick-post-icon"><Briefcase size={18}/></span>
            <span className="flex-1">
              <span className="block font-medium text-sm">Post a job</span>
              <span className="block text-xs text-[#8A8A8A]">Hire another Hooman</span>
            </span>
            <Plus size={14} className="text-[#8A8A8A]"/>
          </Link>
        </div>
      </div>

      {/* Verification banner */}
      {verif && (
        <div className={`card p-6 mb-8 ${verif.verified ? "bg-[#0A0A0A] text-white" : ""}`} data-testid="verification-card">
          <div className="flex items-start gap-4">
            <div className={`h-12 w-12 rounded-full flex items-center justify-center ${verif.verified ? "bg-white/15" : "bg-[#F2F2F0]"}`}>
              <BadgeCheck size={22}/>
            </div>
            <div className="flex-1">
              <p className={`overline ${verif.verified ? "text-white/70" : ""}`}>Verification</p>
              <h3 className="font-heading text-xl mt-1">
                {verif.verified ? "You're a Verified Human" : "Get the Verified badge"}
              </h3>
              <p className={`text-sm mt-1 ${verif.verified ? "text-white/70" : "text-[#4A4A4A]"}`}>
                {verif.rule}
              </p>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 mt-4" data-testid="verification-checklist">
                {Object.entries(verif.checks).map(([k,ok])=>(
                  <div key={k} className={`flex items-center gap-2 text-sm ${verif.verified ? "text-white/90" : ""}`} data-testid={`verif-${k}`}>
                    {ok ? <Check size={14} className={verif.verified?"":"text-[#0A0A0A]"}/> : <Circle size={14} className={verif.verified?"":"text-[#8A8A8A]"}/>}
                    <span className={!ok && !verif.verified ? "text-[#8A8A8A]":""}>{verifLabel(k)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={save} className="space-y-6" data-testid="edit-profile-form">
        <div className="card p-7 space-y-6">
          {/* Cover + avatar drops */}
          <div className="grid sm:grid-cols-[1fr_220px] gap-5 items-start">
            <div>
              <span className="text-sm text-[#4A4A4A] mb-2 block">Cover photo</span>
              <PhotoDrop value={form.cover} onChange={(v)=>setForm({...form, cover:v})} aspect="cover" label="Drop your cover here" testid="edit-cover-drop" maxSize={1800}/>
              {form.cover && (
                <div className="mt-3" data-testid="edit-cover-positioner-wrap">
                  <p className="text-[11px] uppercase tracking-wider text-[#8A8A8A] mb-1.5">Reposition — drag the crosshair to pick the focal point</p>
                  <ImagePositioner
                    src={form.cover}
                    value={form.cover_position || "50% 50%"}
                    onChange={(v)=>setForm({...form, cover_position:v})}
                    aspect="3 / 1"
                    testid="edit-cover-positioner"
                  />
                </div>
              )}
            </div>
            <div>
              <span className="text-sm text-[#4A4A4A] mb-2 block">Profile photo</span>
              <PhotoDrop value={form.avatar} onChange={(v)=>setForm({...form, avatar:v})} aspect="square" label="Drop avatar" testid="edit-avatar-drop" maxSize={800}/>
              {form.avatar && (
                <div className="mt-3" data-testid="edit-avatar-positioner-wrap">
                  <p className="text-[11px] uppercase tracking-wider text-[#8A8A8A] mb-1.5">Reposition</p>
                  <ImagePositioner
                    src={form.avatar}
                    value={form.avatar_position || "50% 50%"}
                    onChange={(v)=>setForm({...form, avatar_position:v})}
                    circular
                    testid="edit-avatar-positioner"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Name"><input className="input" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} data-testid="edit-name"/></Field>
            <Field label="Location"><input className="input" value={form.location} onChange={(e)=>setForm({...form, location:e.target.value})} placeholder="City, Country" data-testid="edit-location"/></Field>
            <Field label="Years of experience" className="sm:col-span-2 max-w-xs"><input type="number" min={0} className="input" value={form.years_experience} onChange={(e)=>setForm({...form, years_experience:e.target.value})} data-testid="edit-experience"/></Field>
          </div>

          <div>
            <p className="text-sm text-[#4A4A4A] mb-2">What do you do? Pick up to 6 identities — combine all of them.</p>
            <CategoryPicker value={form.categories} onChange={(cats)=>setForm({...form, categories: cats, category: cats[0] || ""})} max={6}/>
          </div>

          <Field label="Skills">
            <div className="flex gap-2 mb-2 flex-wrap" data-testid="edit-skills">
              {form.skills.map((s,i)=>(
                <span key={i} className="badge-cat">{s}
                  <button type="button" className="ml-2 text-[#8A8A8A]" onClick={()=>setForm({...form, skills: form.skills.filter((_,j)=>j!==i)})}>×</button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input className="input" value={skillInput} onChange={(e)=>setSkillInput(e.target.value)} placeholder="Add a skill" data-testid="edit-skill-input"
                onKeyDown={(e)=>{if(e.key==='Enter'){e.preventDefault(); addSkill();}}}/>
              <button type="button" className="btn-secondary" onClick={addSkill} data-testid="edit-skill-add">Add</button>
            </div>
          </Field>

          <Field label={<span className="flex items-center justify-between w-full">Bio
            <button type="button" disabled={aiLoading || !form.name} onClick={aiBio} className="btn-ghost text-[#0057FF]" data-testid="ai-bio-btn">
              <Sparkles size={14}/> {aiLoading?"Writing…":"Write with AI"}
            </button>
          </span>}>
            <textarea rows={4} className="input" value={form.bio} onChange={(e)=>setForm({...form, bio:e.target.value})} data-testid="edit-bio"/>
          </Field>

          {err && <p className="text-sm text-[#B84B4B]">{err}</p>}
          <button className="btn-primary" disabled={saving} data-testid="edit-save">{saving?"Saving…":"Save profile"}</button>
        </div>
      </form>

      {/* Payment methods */}
      <div className="mt-12">
        <p className="overline mb-3">Payments</p>
        <h2 className="h-display text-2xl mb-2">How do you want to get paid?</h2>
        <p className="text-[#4A4A4A] mb-5">Pick everything you&apos;d accept — customers choose at checkout.</p>
        <div className="card p-7 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="payment-methods-grid">
          {PAYMENT_METHODS.map(({id,label,Icon}) => {
            const on = form.payment_methods?.includes(id);
            return (
              <button type="button" key={id} data-testid={`pm-toggle-${id}`}
                onClick={()=>{
                  const cur = new Set(form.payment_methods || []);
                  if (cur.has(id)) cur.delete(id); else cur.add(id);
                  if (cur.size === 0) cur.add("card");
                  setForm({...form, payment_methods: Array.from(cur)});
                }}
                className={`flex items-center gap-3 p-4 rounded-2xl border transition-all text-left
                  ${on ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5E3] bg-white hover:bg-[#F2F2F0]"}`}>
                <div className={`h-10 w-10 rounded-full flex items-center justify-center ${on ? "bg-white/15" : "bg-[#F2F2F0]"}`}>
                  <Icon size={18} />
                </div>
                <span className="font-medium">{label}</span>
              </button>
            );
          })}
        </div>
        <button onClick={save} disabled={saving} className="btn-primary mt-4" data-testid="payment-save">Save payment methods</button>
      </div>

      {/* Payouts entry */}
      <div className="mt-8 card p-6 flex flex-wrap items-center gap-4" data-testid="payouts-entry">
        <div className="h-12 w-12 rounded-full bg-[#0A0A0A] text-white flex items-center justify-center">
          <Sparkles size={18}/>
        </div>
        <div className="flex-1 min-w-[200px]">
          <p className="font-medium">Payout methods</p>
          <p className="text-sm text-[#4A4A4A]">Set up Stripe Connect, PayPal, Venmo, bank, Cash App or Wise — encrypted &amp; secure.</p>
        </div>
        <a href="/settings/payouts" className="btn-secondary" data-testid="payouts-entry-cta">Manage payouts</a>
      </div>

      {/* Email notifications preferences */}
      <NotificationPrefs form={form} setForm={setForm} save={save}/>

      {/* Services */}
      <div className="mt-12">
        <p className="overline mb-3">Your services</p>
        <h2 className="h-display text-2xl mb-5">What can people book?</h2>
        <div className="grid gap-3" data-testid="services-list">
          {services.map(s=>(
            <div key={s.id} className="card p-4 flex items-center gap-4" data-testid={`service-row-${s.id}`}>
              {s.image
                ? <img src={s.image} alt="" className="h-16 w-16 rounded-xl object-cover"/>
                : <div className="h-16 w-16 rounded-xl bg-[#F2F2F0]"/>}
              <div className="flex-1 min-w-0">
                <p className="font-medium flex items-center gap-2">
                  {s.title} — ${s.price}
                  {s.delivery_type === "online" && (
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#0A0A0A] text-white inline-flex items-center gap-1">
                      Online
                    </span>
                  )}
                </p>
                <p className="text-sm text-[#4A4A4A] line-clamp-1">{s.description}</p>
              </div>
              <button type="button" onClick={()=>editService(s)} className="btn-ghost" data-testid={`service-edit-${s.id}`} aria-label="Edit service"><Edit3 size={16}/></button>
              <button onClick={()=>removeService(s.id)} className="btn-ghost text-[#B84B4B]" data-testid={`service-remove-${s.id}`}><Trash2 size={16}/></button>
            </div>
          ))}
        </div>
        <form onSubmit={addService} className="card p-7 mt-4 grid sm:grid-cols-[200px_1fr] gap-5" data-testid="service-form">
          <div>
            <span className="text-sm text-[#4A4A4A] mb-2 block">Service photo</span>
            <PhotoDrop value={svcForm.image} onChange={(v)=>setSvcForm({...svcForm, image:v})} aspect="square" label="Drop photo" testid="service-image-drop" maxSize={1000}/>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Title"><input required className="input" value={svcForm.title} onChange={(e)=>setSvcForm({...svcForm, title:e.target.value})} data-testid="service-title"/></Field>
            <Field label="Duration (min)"><input type="number" min={15} className="input" value={svcForm.duration_min} onChange={(e)=>setSvcForm({...svcForm, duration_min:e.target.value})} data-testid="service-duration"/></Field>
            <Field label="Description" className="sm:col-span-2"><textarea required rows={2} className="input" value={svcForm.description} onChange={(e)=>setSvcForm({...svcForm, description:e.target.value})} data-testid="service-description"/></Field>
            <Field label={<span className="flex items-center justify-between w-full">Price (USD)
              <button type="button" disabled={aiPriceLoading || !svcForm.title} onClick={suggestPrice} className="btn-ghost text-[#0057FF]" data-testid="ai-pricing-btn">
                <Sparkles size={14}/> {aiPriceLoading?"…":"Suggest with AI"}
              </button>
            </span>}>
              <input type="number" min={1} required className="input" value={svcForm.price} onChange={(e)=>setSvcForm({...svcForm, price:e.target.value})} data-testid="service-price"/>
              {aiPricing && <p className="text-xs text-[#4A4A4A] mt-2 whitespace-pre-line" data-testid="ai-pricing-result">{aiPricing}</p>}
            </Field>
            <Field label="How is it delivered?" className="sm:col-span-2">
              <div className="grid grid-cols-2 gap-2" data-testid="service-delivery-type">
                {[
                  { id: "in_person", label: "In person",  hint: "Customer comes to you (or you go to them)" },
                  { id: "online",    label: "Online (Jitsi video room)", hint: "Auto-creates a private video room for every booking" },
                ].map(opt => (
                  <label key={opt.id}
                    className={`card p-3 cursor-pointer transition-colors ${svcForm.delivery_type === opt.id ? "border-[#0A0A0A] bg-[#FAFAF7]" : "hover:border-[#C0C0BD]"}`}
                    data-testid={`service-delivery-${opt.id}`}>
                    <input type="radio" name="delivery_type" value={opt.id}
                      checked={svcForm.delivery_type === opt.id}
                      onChange={()=>setSvcForm({...svcForm, delivery_type: opt.id})}
                      className="sr-only"/>
                    <p className="text-sm font-medium">{opt.label}</p>
                    <p className="text-[11px] text-[#8A8A8A] mt-1">{opt.hint}</p>
                  </label>
                ))}
              </div>
            </Field>
            <div className="sm:col-span-2 flex items-center gap-2">
              <button className="btn-primary" data-testid="service-add">
                <Plus size={16}/> {editingSvcId ? "Save changes" : "Add service"}
              </button>
              {editingSvcId && (
                <button type="button" onClick={cancelEditService} className="btn-ghost" data-testid="service-edit-cancel">
                  <X size={14}/> Cancel
                </button>
              )}
            </div>
          </div>
        </form>
      </div>

      {/* Portfolio */}
      <div className="mt-12">
        <p className="overline mb-3">Your portfolio</p>
        <h2 className="h-display text-2xl mb-5">Show your work to the feed.</h2>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-4" data-testid="portfolio-grid">
          {portfolio.map((p)=>(
            <div key={p.id} className="relative group aspect-square overflow-hidden rounded-2xl bg-[#F2F2F0]"
                 data-testid={`portfolio-tile-${p.id}`}>
              <img src={p.image} alt="" className="w-full h-full object-cover"/>
              <button
                type="button"
                onClick={()=>removePortfolio(p.id)}
                aria-label="Delete portfolio image"
                title="Delete"
                data-testid={`portfolio-delete-${p.id}`}
                className="absolute top-2 right-2 h-8 w-8 rounded-full bg-black/60 text-white inline-flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 hover:bg-[#B84B4B] transition-all"
              >
                <Trash2 size={14}/>
              </button>
              {p.caption && p.caption !== "New work" && (
                <p className="absolute bottom-0 inset-x-0 p-2 text-xs text-white bg-gradient-to-t from-black/70 to-transparent line-clamp-2">{p.caption}</p>
              )}
            </div>
          ))}
          {portfolio.length === 0 && (
            <p className="col-span-full text-sm text-[#8A8A8A]">No portfolio yet — add your first piece below.</p>
          )}
        </div>
        <form onSubmit={addPortfolio} className="card p-7 grid sm:grid-cols-[260px_1fr] gap-5 items-start" data-testid="portfolio-form">
          <PhotoDrop value={portImage} onChange={setPortImage} aspect="square" label="Drop a new piece of work" testid="portfolio-image-drop" maxSize={1600}/>
          <div className="space-y-3">
            <Field label="Caption (optional)">
              <textarea rows={3} className="input" value={portCaption} onChange={(e)=>setPortCaption(e.target.value)} placeholder="A note about this piece" data-testid="portfolio-caption"/>
            </Field>
            <button className="btn-primary" disabled={!portImage} data-testid="portfolio-add"><Plus size={16}/> Add to portfolio</button>
          </div>
        </form>
      </div>

      {/* Video reel */}
      <div className="mt-12">
        <p className="overline mb-3">Your reel</p>
        <h2 className="h-display text-2xl mb-5">Bring your craft to life — on video.</h2>
        <VideoReel
          initialVideos={user?.videos || []}
          onChange={()=>{ refreshUser?.(); }}
        />
      </div>
      {/* Sales type */}
      <div className="mt-12">
        <p className="overline mb-3">How you sell</p>
        <h2 className="h-display text-2xl mb-2">Bookings, products, or both?</h2>
        <p className="text-[#4A4A4A] mb-5">Pick what shows up on your profile. You can switch any time.</p>
        <div className="card p-5 grid sm:grid-cols-2 gap-3" data-testid="sales-toggles">
          <button type="button" onClick={()=>setForm({...form, sells_bookings: !form.sells_bookings})}
            className={`p-5 rounded-2xl border text-left transition-all ${form.sells_bookings?"bg-[#0A0A0A] text-white border-[#0A0A0A]":"bg-white border-[#E5E5E3] hover:bg-[#F2F2F0]"}`}
            data-testid="sells-bookings-toggle">
            <p className="font-heading text-lg">Bookings</p>
            <p className={`text-sm mt-1 ${form.sells_bookings?"text-white/70":"text-[#4A4A4A]"}`}>One-on-one sessions, calendar-based.</p>
          </button>
          <button type="button" onClick={()=>setForm({...form, sells_products: !form.sells_products})}
            className={`p-5 rounded-2xl border text-left transition-all ${form.sells_products?"bg-[#0A0A0A] text-white border-[#0A0A0A]":"bg-white border-[#E5E5E3] hover:bg-[#F2F2F0]"}`}
            data-testid="sells-products-toggle">
            <p className="font-heading text-lg">Products</p>
            <p className={`text-sm mt-1 ${form.sells_products?"text-white/70":"text-[#4A4A4A]"}`}>Digital downloads, presets, art prints, courses.</p>
          </button>
        </div>
        <button onClick={save} className="btn-primary mt-4" data-testid="sales-save">Save</button>
      </div>

      {/* Products / Marketplace */}
      {form.sells_products && (
        <div className="mt-12" data-testid="products-section">
          <p className="overline mb-3">Your products</p>
          <h2 className="h-display text-2xl mb-5">What can people buy from you?</h2>
          <div className="grid sm:grid-cols-2 gap-3" data-testid="products-list">
            {products.map(p=>(
              <div key={p.id} className="card p-4 flex items-center gap-4" data-testid={`product-row-${p.id}`}>
                {p.image
                  ? <img src={p.image} alt="" className="h-16 w-16 rounded-xl object-cover"/>
                  : <div className="h-16 w-16 rounded-xl bg-[#F2F2F0]"/>}
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{p.title}</p>
                  <p className="text-sm text-[#4A4A4A]">${p.price} · {p.product_type}</p>
                </div>
                <button type="button" onClick={()=>editProduct(p)} className="btn-ghost" data-testid={`product-edit-${p.id}`} aria-label="Edit product"><Edit3 size={16}/></button>
                <button onClick={()=>removeProduct(p.id)} className="btn-ghost text-[#B84B4B]" data-testid={`product-remove-${p.id}`}><Trash2 size={16}/></button>
              </div>
            ))}
          </div>
          <form onSubmit={addProduct} className="card p-7 mt-4 grid sm:grid-cols-[240px_1fr] gap-5" data-testid="product-form">
            <div>
              <div className="flex items-baseline justify-between">
                <span className="text-sm text-[#4A4A4A] block">Photos · {(prodForm.images || []).length}/5</span>
                <span className="text-[10px] text-[#8A8A8A]">First = cover</span>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2" data-testid="product-images-grid">
                {(prodForm.images || []).map((src, k) => (
                  <div key={k} className={`relative aspect-square rounded-xl overflow-hidden bg-[#F2F2F0] ${k===0 ? "ring-2 ring-[#0A0A0A]" : ""}`}>
                    <img src={src} alt="" className="w-full h-full object-cover"/>
                    {k===0 && <span className="absolute top-1 left-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#0A0A0A] text-white">Cover</span>}
                    <button type="button" onClick={()=>setProdForm(f=>({...f, images: f.images.filter((_,i)=>i!==k), image: (k===0 ? (f.images[1] || "") : f.image)}))}
                      className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white inline-flex items-center justify-center hover:bg-[#B84B4B]"
                      data-testid={`product-image-rm-${k}`}>
                      <X size={11}/>
                    </button>
                  </div>
                ))}
                {(prodForm.images || []).length < 5 && (
                  <div className="aspect-square">
                    <PhotoDrop
                      multiple
                      onPick={(url)=>setProdForm(f=>{
                        if ((f.images||[]).length >= 5) return f;
                        const nextImgs = [...(f.images||[]), url];
                        return { ...f, images: nextImgs, image: f.image || nextImgs[0] };
                      })}
                      aspect="square"
                      label={(prodForm.images || []).length === 0 ? "Add photos" : "Add more"}
                      testid="product-image-drop"
                      maxSize={1400}
                    />
                  </div>
                )}
              </div>
              <p className="text-[10px] text-[#8A8A8A] mt-2">Up to 5. Drag-select multiple at once.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Title"><input required className="input" value={prodForm.title} onChange={(e)=>setProdForm({...prodForm, title:e.target.value})} data-testid="product-title"/></Field>
              <Field label="Price (USD)"><input type="number" min={1} required className="input" value={prodForm.price} onChange={(e)=>setProdForm({...prodForm, price:e.target.value})} data-testid="product-price"/></Field>
              <Field label="Description" className="sm:col-span-2">
                <textarea required rows={3} className="input" value={prodForm.description} onChange={(e)=>setProdForm({...prodForm, description:e.target.value})} placeholder="What is it? Format, length, what's included…" data-testid="product-description"/>
              </Field>
              <Field label="Type">
                <select className="input" value={prodForm.product_type} onChange={(e)=>setProdForm({...prodForm, product_type:e.target.value})} data-testid="product-type">
                  <option value="digital">Digital download</option>
                  <option value="physical">Physical</option>
                  <option value="consultation">Consultation</option>
                </select>
              </Field>
              <Field label="Download / file URL (for digital)">
                <input className="input" value={prodForm.file_url} onChange={(e)=>setProdForm({...prodForm, file_url:e.target.value})} placeholder="https://…" data-testid="product-file"/>
              </Field>

              {/* Shipping (physical) + Tax (any) — buyer sees the full breakdown at checkout */}
              {prodForm.product_type === "physical" && (
                <div className="sm:col-span-2 rounded-2xl border border-[#E5E5E3] bg-[#FAFAF8] p-4" data-testid="product-shipping-block">
                  <p className="text-xs uppercase tracking-wider text-[#4A4A4A] mb-3">Shipping</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="Flat shipping fee (USD)">
                      <input type="number" min="0" step="0.01" className="input" value={prodForm.shipping_cost}
                        onChange={(e)=>setProdForm({...prodForm, shipping_cost:e.target.value})} placeholder="e.g. 5" data-testid="product-shipping-cost"/>
                    </Field>
                    <Field label="Free shipping over (USD, optional)">
                      <input type="number" min="0" step="0.01" className="input" value={prodForm.shipping_free_over}
                        onChange={(e)=>setProdForm({...prodForm, shipping_free_over:e.target.value})} placeholder="e.g. 75" data-testid="product-shipping-free-over"/>
                    </Field>
                    <Field label="Ships from">
                      <input className="input" value={prodForm.ships_from}
                        onChange={(e)=>setProdForm({...prodForm, ships_from:e.target.value})} placeholder="Lisbon, Portugal" data-testid="product-ships-from"/>
                    </Field>
                    <Field label="Ships to">
                      <input className="input" value={prodForm.ships_to}
                        onChange={(e)=>setProdForm({...prodForm, ships_to:e.target.value})} placeholder="Worldwide · EU + US" data-testid="product-ships-to"/>
                    </Field>
                    <Field label="Estimated delivery" className="sm:col-span-2">
                      <input className="input" value={prodForm.estimated_delivery}
                        onChange={(e)=>setProdForm({...prodForm, estimated_delivery:e.target.value})} placeholder="3–5 business days" data-testid="product-eta"/>
                    </Field>
                  </div>
                </div>
              )}

              <div className="sm:col-span-2 rounded-2xl border border-[#E5E5E3] bg-[#FAFAF8] p-4" data-testid="product-tax-block">
                <p className="text-xs uppercase tracking-wider text-[#4A4A4A] mb-3">Tax</p>
                <div className="grid sm:grid-cols-2 gap-3 items-end">
                  <Field label="Tax rate (%)">
                    <input type="number" min="0" max="100" step="0.01" className="input" value={prodForm.tax_rate}
                      onChange={(e)=>setProdForm({...prodForm, tax_rate:e.target.value})} placeholder="e.g. 8.25" data-testid="product-tax-rate"/>
                  </Field>
                  <label className="flex items-center gap-2 text-sm pb-2 cursor-pointer">
                    <input type="checkbox" checked={!!prodForm.tax_included}
                      onChange={(e)=>setProdForm({...prodForm, tax_included:e.target.checked})}
                      data-testid="product-tax-included"/>
                    <span>Price already includes tax (don&apos;t add it at checkout)</span>
                  </label>
                </div>
                <p className="text-[11px] text-[#8A8A8A] mt-2">
                  Tip: leave tax at <b>0</b> if you don&apos;t collect it. Buyers see subtotal + shipping + tax on the product page — no surprises at Stripe.
                </p>
              </div>
              <div className="sm:col-span-2 flex items-center gap-2">
                <button className="btn-primary" data-testid="product-add">
                  <Plus size={16}/> {editingProdId ? "Save changes" : "Add to shop"}
                </button>
                {editingProdId && (
                  <button type="button" onClick={cancelEditProduct} className="btn-ghost" data-testid="product-edit-cancel">
                    <X size={14}/> Cancel
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Field({ label, children, className="" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-sm text-[#4A4A4A] mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}

function verifLabel(k) {
  return ({
    name: "Real name",
    avatar: "Profile photo",
    location: "Where you work",
    categories: "At least one profession",
    bio_60_chars: "Bio with 60+ characters",
    at_least_1_service: "At least one bookable service",
  })[k] || k;
}

// -------------------------------------------------------------------------
// Email notification preferences
// -------------------------------------------------------------------------
const EMAIL_PREF_ROWS = [
  { k: "message",     label: "Direct messages & application chats", tip: "Recommended — most people want these instantly.", defaultOn: true },
  { k: "booking",     label: "Booking / rental / order confirmations", tip: "Money-related, always emailed by default.", defaultOn: true },
  { k: "application", label: "Job applications & status updates", tip: "As an employer or applicant.", defaultOn: true },
  { k: "interview",   label: "Interview scheduled / rescheduled",  tip: "Never miss an interview time.", defaultOn: true },
  { k: "follow",      label: "New followers",                       tip: "New people connecting with you.", defaultOn: true },
  { k: "review",      label: "New reviews on your profile",         tip: "So you can reply while the moment is fresh.", defaultOn: true },
  { k: "like",        label: "Likes on your posts",                 tip: "Can get noisy — off by default.", defaultOn: false },
  { k: "save",        label: "Someone saved one of your posts",     tip: "A signal they might book — off by default.", defaultOn: false },
];

function NotificationPrefs({ form, setForm, save }) {
  const prefs = form.email_prefs || {};
  const toggle = (k) => {
    setForm(f => ({ ...f, email_prefs: { ...f.email_prefs, [k]: !f.email_prefs?.[k] } }));
  };
  return (
    <div id="notifications" className="mt-8 card p-6" data-testid="notif-prefs-card">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4">
        <div>
          <p className="overline">Email notifications</p>
          <h3 className="text-lg font-heading mt-1">What should we email you about?</h3>
          <p className="text-sm text-[#4A4A4A] mt-1">You&apos;ll always see these in-app — email is a nudge for the ones that matter.</p>
        </div>
        <button type="button" onClick={save} className="btn-primary text-sm" data-testid="notif-prefs-save">Save preferences</button>
      </div>
      <div className="divide-y divide-[#F2F2F0]">
        {EMAIL_PREF_ROWS.map(row => {
          const on = prefs[row.k] ?? row.defaultOn;
          return (
            <div key={row.k} className="flex items-center gap-3 py-3" data-testid={`notif-row-${row.k}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{row.label}</p>
                <p className="text-xs text-[#8A8A8A]">{row.tip}</p>
              </div>
              <button type="button" onClick={() => toggle(row.k)}
                aria-pressed={on}
                data-testid={`notif-toggle-${row.k}`}
                className={`inline-flex items-center h-6 w-11 rounded-full transition-colors ${on ? "bg-[#0057FF]" : "bg-[#E5E5E3]"}`}>
                <span className={`h-5 w-5 bg-white rounded-full shadow transform transition-transform ${on ? "translate-x-5" : "translate-x-0.5"}`}/>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
